import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Packages
import { Button, Popconfirm, Space, Spin, Table, Tooltip, Typography } from 'antd';
import { EyeFilled, DeleteFilled, EditFilled, QuestionCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Actions
import { get_all_actions, action_delete } from '../../../redux/actions/actionsActions';

const ActionsTable = (props) => {
	const { messageApi, setIsModalOpen, setUpdate, setActionToUpdate, pageSize, setPageSize, skip, setSkip } = props;

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const navigate = useNavigate();

	const { all_actions_loading, all_actions, action_delete_loading } = useSelector(state => state.actions);

	const update = (record) => {
		setIsModalOpen(true);
		setUpdate(true);
		setActionToUpdate(record);
	}

	const deleteAction = (record) => {
		let newSkip = skip;

		if ((all_actions.count - 1) % pageSize === 0) {
			newSkip -= pageSize;
			setSkip(newSkip);
			localStorage.setItem('actionsSkip', newSkip);
		}

		const filters = { skip: newSkip, limit: pageSize };
		dispatch(action_delete(messageApi, record._id.$oid, filters));
	}

	const columns = [
		{
			key: 'name',
			title: t('form.label.name'),
			dataIndex: 'name',
			render: (_, record) => (
				<Typography.Text copyable={{ text: record._id.$oid, tooltips: [t('table.copyableText.copyId'), t('table.copyableText.copiedId')] }}>
					{record.name}
				</Typography.Text>
			)
		},
		{
			key: 'description',
			title: t('form.label.description'),
			dataIndex: 'description'
		},
		{
			key: 'action',
			title: t('table.headers.actions'),
			dataIndex: 'action',
			render: (_, record) => (
				<Space>
					<Tooltip title={t('buttons.details')}>
						<Button
							type='primary'
							shape='circle'
							size='large'
							icon={<EyeFilled />}
							onClick={() => navigate(`/actions/${record.key}/details`)}
						/>
					</Tooltip>
					<Tooltip title={t('buttons.edit')}>
						<Button
							type='primary'
							style={{ backgroundColor: '#80B4B9' }}
							shape='circle'
							size='large'
							icon={<EditFilled />}
							onClick={() => update(record)}
						/>
					</Tooltip>
					<Popconfirm
						title={t('actions.delete.confirmTitle')}
						description={t('actions.delete.confirmText')}
						icon={<QuestionCircleFilled style={{ color: '#1677FF' }} />}
						onConfirm={() => deleteAction(record)}
						okButtonProps={{
							loading: action_delete_loading
						}}
						okText={t('buttons.confirm')}
						cancelText={t('buttons.cancel')}
					>
						<Button
							type='primary'
							shape='circle'
							size='large'
							danger
							icon={<DeleteFilled />}
						/>
					</Popconfirm>
				</Space>
			)
		}
	];

	const handleTableChange = (pagination, filters, sorter) => {
		const skip = (pagination.current - 1) * pagination.pageSize;
		setSkip(skip);
		localStorage.setItem('actionsSkip', skip);

		const limit = pagination.pageSize;
		setPageSize(limit);
		localStorage.setItem('actionsPageSize', limit);

		const actionsFilters = { skip, limit };
		dispatch(get_all_actions(actionsFilters));
	};

	return (
		<Spin spinning={all_actions_loading}>
			<Table
				columns={columns}
				dataSource={all_actions.actions}
				onChange={handleTableChange}
				scroll={{
					x: 'auto'
				}}
				pagination={{
					pageSize: pageSize, 
					total: all_actions.count,
					current: (skip / pageSize) + 1,
					showSizeChanger: true,
					showTotal: (total, range) => `${range[0]} - ${range[1]} ${t('table.pagination.of')} ${total}`,
					showTitle: false,
					responsive: true,
					locale: { items_per_page: `/ ${t('table.pagination.page')}` }
				}}
			/>
		</Spin>
	)
}

ActionsTable.propTypes = {
	messageApi: PropTypes.object.isRequired,
	setIsModalOpen: PropTypes.func.isRequired,
	setUpdate: PropTypes.func.isRequired,
	setActionToUpdate: PropTypes.func.isRequired,
	pageSize: PropTypes.number.isRequired,
	setPageSize: PropTypes.func.isRequired,
	skip: PropTypes.number.isRequired,
	setSkip: PropTypes.func.isRequired
}

export default ActionsTable;
